import { Button, Input } from "@/components/ui/primitives";
import { useDocumentCategories } from "@/hooks/use-document-categories";
import { cn } from "@/packages/utils";
import type { Id } from "@/packages/types";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, FolderOpen } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCategoryLucideIcon } from "./category-icon-map";

interface CategoryPickerProps {
  selectedCategoryId?: Id<"documentCategories">;
  onSelect: (categoryId: Id<"documentCategories">) => void;
  disabled?: boolean;
}

export function CategoryPicker({
  selectedCategoryId,
  onSelect,
  disabled = false,
}: CategoryPickerProps) {
  const { t } = useTranslation("documents");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { categories, isLoading } = useDocumentCategories();

  const selectedCategory = useMemo(
    () => categories.find((c) => c._id === selectedCategoryId),
    [categories, selectedCategoryId],
  );

  const filteredCategories = useMemo(() => {
    if (!search) return categories;
    const lowerSearch = search.toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(lowerSearch));
  }, [categories, search]);

  const handleSelect = (categoryId: Id<"documentCategories">) => {
    onSelect(categoryId);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            !selectedCategory && "text-muted-foreground",
          )}
          disabled={disabled || isLoading}
        >
          <div className="flex items-center gap-2 overflow-hidden min-w-0">
            {selectedCategory ? (
              <>
                {(() => {
                  const IconComponent = getCategoryLucideIcon(
                    selectedCategory.icon,
                  );
                  return IconComponent ? (
                    <IconComponent className="h-4 w-4 shrink-0" />
                  ) : (
                    <FolderOpen className="h-4 w-4 shrink-0" />
                  );
                })()}
                <span className="truncate">{selectedCategory.name}</span>
              </>
            ) : (
              <>
                <FolderOpen className="h-4 w-4 shrink-0" />
                <span className="truncate">{t("upload.selectCategory")}</span>
              </>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="w-[300px] p-0 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl z-[9999] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          align="start"
          sideOffset={4}
        >
          <div className="border-b border-gray-200 dark:border-gray-800 p-2 bg-white dark:bg-gray-950">
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="max-h-[300px] overflow-auto p-1 bg-white dark:bg-gray-950">
            {filteredCategories.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {isLoading ? "Loading categories..." : "No categories found"}
              </div>
            ) : (
              filteredCategories.map((category) => {
                const IconComponent = getCategoryLucideIcon(category.icon);
                const isSelected = category._id === selectedCategoryId;

                return (
                  <button
                    key={category._id}
                    type="button"
                    onClick={() => handleSelect(category._id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                      isSelected && "bg-accent text-accent-foreground",
                    )}
                  >
                    {IconComponent ? (
                      <IconComponent className="h-4 w-4" />
                    ) : (
                      <FolderOpen className="h-4 w-4" />
                    )}
                    <span className="flex-1 text-left">{category.name}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
